import type { HTMLProps, RefCallback } from 'react';
import { Fragment } from 'react';
import type { ReactSliderProps } from 'react-slider';
import ReactSlider from 'react-slider';

import { generateDataTestId } from '@/lib/tests';

import * as S from './RangeSlider.styles';

interface HTMLPropsWithRefCallback<T> extends HTMLProps<T> {
  ref: RefCallback<T>;
}

type RenderThumb = (
  props: HTMLPropsWithRefCallback<HTMLDivElement>,
  state: { index: number; value: [number, number]; valueNow: number },
  label?: string,
) => JSX.Element;

const renderThumb: RenderThumb = (props, state, label) => (
  <Fragment key={`thumb-container-${state.index}`}>
    <S.Thumb
      {...props}
      key={`thumb-${state.index}`}
      data-test-id='RangeSliderThumb'
      {...generateDataTestId('slider', `thumb-${state.index}`)}
      as={undefined}
    ></S.Thumb>
    <S.ThumbLabel
      key={`thumb-label-${state.index}`}
      className={`thumb-${state.index === 0 ? 'left' : 'right'}`}
    >
      {state.valueNow} {label}
    </S.ThumbLabel>
  </Fragment>
);

const renderTrack: ReactSliderProps<[number, number]>['renderTrack'] = (
  props,
  state,
) => (
  <S.TrackWrapper
    {...props}
    key={`track-${state.index}`}
    as={undefined}
    data-test-id='RangeSliderTrackWrapper'
  >
    <S.Track data-test-id='RangeSliderTrack' index={state.index} />
  </S.TrackWrapper>
);

export type RangeSliderProps = ReactSliderProps<[number, number]> & {
  label?: string;
};

export const RangeSlider = ({ label, ...props }: RangeSliderProps) => (
  <ReactSlider
    data-test-id='RangeSlider'
    renderThumb={(props, state) => renderThumb(props, state, label)}
    renderTrack={renderTrack}
    {...props}
  />
);
